import { Col, Form, Input, Row } from "antd";
import CustomInput from "./inputWidgets/CustomInput";
import {
  brandKeyFromNumber,
  CardBrandIcon,
  formatCardExpirationInput,
  formatCardNumberInput,
  getCardExpirationError,
  getCvvMaxLength,
  parseCardExpiration,
} from "../utils/cardPaymentUtils";

/**
 * Shared Intuit card fields (add-card + Make Payment new card).
 */
export default function IntuitCardFields({ form, cvvInputRef, children }) {
  const cardNumberWatch = Form.useWatch("CardNumber", form);
  const detectedBrandKey = brandKeyFromNumber(cardNumberWatch);
  const cvvMax = getCvvMaxLength(cardNumberWatch);

  return (
    <Row gutter={[16, 8]}>
      <Col span={24}>
        <CustomInput
          label="Name on card"
          name="CardHolderName"
          required
          placeholder="Cardholder name"
        />
      </Col>
      <Col span={24}>
        <Form.Item
          label={
            <span>
              Card number
              <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="CardNumber"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          required={false}
          rules={[
            { required: true, message: "Card number is required" },
            {
              validator: (_, value) => {
                const digits = String(value || "").replace(/\D/g, "");
                if (!digits) return Promise.resolve();
                if (digits.length < 13 || digits.length > 19) {
                  return Promise.reject(new Error("Enter a valid card number"));
                }
                return Promise.resolve();
              },
            },
          ]}
          getValueFromEvent={(e) => formatCardNumberInput(e?.target?.value)}
        >
          <Input
            placeholder="•••• •••• •••• ••••"
            inputMode="numeric"
            autoComplete="cc-number"
            style={{ height: 32 }}
            suffix={
              <CardBrandIcon
                brandKey={detectedBrandKey}
                className="shrink-0"
              />
            }
          />
        </Form.Item>
      </Col>
      <Col xs={12} span={12}>
        <Form.Item
          label={
            <span>
              Expires on
              <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="ExpDate"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          required={false}
          rules={[
            { required: true, message: "Expires on is required" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const parsed = parseCardExpiration(value);
                if (!parsed) {
                  return Promise.reject(new Error("Enter as MM/YY"));
                }
                const err = getCardExpirationError(parsed.month, parsed.year);
                if (err) return Promise.reject(new Error(err));
                return Promise.resolve();
              },
            },
          ]}
          getValueFromEvent={(e) => formatCardExpirationInput(e?.target?.value)}
        >
          <Input
            placeholder="MM/YY"
            inputMode="numeric"
            autoComplete="cc-exp"
            style={{ height: 32 }}
            onChange={(e) => {
              const digits = String(e?.target?.value || "").replace(/\D/g, "");
              if (digits.length >= 4) {
                cvvInputRef?.current?.focus?.();
              }
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={12} span={12}>
        <Form.Item
          label={
            <span>
              CVV
              <span style={{ color: "red" }}>*</span>
            </span>
          }
          name="Cvc"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          required={false}
          rules={[
            { required: true, message: "CVV is required" },
            {
              validator: (_, value) => {
                const digits = String(value || "").replace(/\D/g, "");
                if (!digits) return Promise.resolve();
                if (detectedBrandKey === "amex") {
                  if (digits.length !== 4) {
                    return Promise.reject(
                      new Error("American Express cards require 4 digits cvc"),
                    );
                  }
                  return Promise.resolve();
                }
                if (digits.length < 3 || digits.length > cvvMax) {
                  return Promise.reject(
                    new Error(
                      cvvMax === 4
                        ? "Enter 3 or 4-digit CVV"
                        : "Enter 3-digit CVV",
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          getValueFromEvent={(e) =>
            String(e?.target?.value || "")
              .replace(/\D/g, "")
              .slice(0, cvvMax)
          }
        >
          <Input
            placeholder={cvvMax === 4 ? "1234" : "123"}
            inputMode="numeric"
            autoComplete="cc-csc"
            style={{ height: 32 }}
            maxLength={cvvMax}
            ref={cvvInputRef}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item
          label={<span>ZIP / postal code</span>}
          name="PostalCode"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          required={false}
          rules={[
            {
              validator: (_, value) => {
                const v = String(value || "").trim();
                if (!v) return Promise.resolve();
                if (!/^[A-Za-z0-9][A-Za-z0-9\s-]{1,11}$/.test(v)) {
                  return Promise.reject(
                    new Error("Enter a valid ZIP / postal code"),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          getValueFromEvent={(e) =>
            String(e?.target?.value || "")
              .toUpperCase()
              .replace(/[^A-Z0-9\s-]/g, "")
              .slice(0, 12)
          }
        >
          <Input
            placeholder="ZIP / postal code"
            autoComplete="postal-code"
            style={{ height: 32 }}
          />
        </Form.Item>
      </Col>
      {children}
    </Row>
  );
}
